import React, { useEffect, useState } from 'react'
import { Classes, Tag, Tree, TreeNodeInfo } from '@blueprintjs/core'
import { isEmpty } from '../../util/helpers'
import { useTreeFunctions } from '../../hooks/useTreeFunctions'
import { capitalCase } from 'change-case'
import { Link } from 'react-router-dom'
import { money } from '../../util/formatters'
import { TreeProps } from './KeywordsTree'
import { PromoDryRunNegativeTarget, PromoDryRunTarget } from '../../types/keywords_tree'

export const generateMatchType = (matchType: any) => {
    if (matchType == '' || matchType == null || !matchType) return ''

    matchType = capitalCase(matchType).toLowerCase()
    switch (matchType) {
        case 'asin same as':
            return (
                <Tag style={{ display: 'inline-flex' }} intent="success">
                    <b>{matchType}</b>
                </Tag>
            )
        default:
            return matchType
    }
}

interface Item {
    matchType: string
    asin: string
    bid: number | null
    adGroupName: string
    campaignName: string
    campaignId: number
    adGroupId: number
}

type Targets = { [key: string]: Item[] }

export const TargetsTree = <T extends PromoDryRunTarget | PromoDryRunNegativeTarget>({
    data,
    title,
    showAdGroup = true,
    bid = true,
}: TreeProps<T>): JSX.Element => {
    const [targets, setTargets] = useState<Targets>({})
    const { nodes, setNodes, handleNodeClick, handleNodeCollapse, handleNodeExpand } =
        useTreeFunctions()

    const load = () => {
        if (data.items == null || data.count == 0) return

        const output: Targets = {}
        data.items.forEach((item: T) => {
            const createdItem: Item = {
                matchType: item.expression[0].type,
                asin: item.expression[0].value.toUpperCase() ?? 'N/A',
                bid: 'bid' in item ? item.bid : null,
                adGroupName: item.adGroupData?.name,
                campaignName: item.campaignData.name,
                campaignId: item.campaignId,
                adGroupId: item.adGroupId,
            }

            const key = item.campaignId + ':' + item.adGroupId
            if (output.hasOwnProperty(key)) {
                output[key].push(createdItem)
            } else {
                output[key] = [createdItem]
            }
        })
        setTargets(output)
    }

    const generateLabel = (items: Item[]) => {
        return (
            <p>
                {items.length == data.count ? 'All' : items.length} for Campaign{' '}
                <Link to={'/campaigns/' + items[0].campaignId}>{items[0].campaignName}</Link>
                {showAdGroup && ', '}
                {showAdGroup ? (
                    <Link to={'/ad-groups/' + items[0].adGroupId}>{items[0].adGroupName}</Link>
                ) : (
                    ''
                )}
            </p>
        )
    }

    useEffect(() => {
        if (!isEmpty(data)) {
            load()
        }
    }, [data])

    useEffect(() => {
        renderKeywordTree()
    }, [targets])

    const renderKeywordTree = () => {
        const output: TreeNodeInfo[] = []

        Object.keys(targets).forEach((key) => {
            const items = targets[key]

            const currentKey = output.length
            const childNodes: TreeNodeInfo[] = []
            items.forEach((item, i) => {
                childNodes.push({
                    id: currentKey + '-' + i,
                    hasCaret: false,
                    isExpanded: true,
                    label: (
                        <span>
                            {generateMatchType(item.matchType)}{' '}
                            <span>
                                {' '}
                                - {item.asin ?? 'N/A'}
                                {bid && <span> - bid {money(item.bid)}</span>}
                            </span>
                        </span>
                    ),
                })
            })

            output.push({
                id: key,
                hasCaret: true,
                label: generateLabel(items),
                isExpanded: true,
                childNodes,
            })
        })

        setNodes(output)
    }

    return (
        <div className={'flex justify-center'}>
            <div
                className={Classes.ELEVATION_0 + ' p-4 dry-run rounded shadow bg-white'}
                style={{ maxWidth: 1200, minWidth: 1200 }}
            >
                <h2 className="text-xl font-bold pb-2">{title}</h2>
                <Tree
                    onNodeClick={handleNodeClick}
                    onNodeCollapse={handleNodeCollapse}
                    onNodeExpand={handleNodeExpand}
                    contents={nodes}
                />
            </div>
        </div>
    )
}
